import { FC, useCallback, useState } from 'react'
import { IoMdArrowBack } from 'react-icons/io'
import { Form, Formik, FormikProps } from 'formik'
import HeadingView from '../HeadingView/HeadingView'
import BackButton from '../BackButton/BackButton'
import { Button, Flex } from '@chakra-ui/react'
import TextInput from '../../FormElements/TextInput'
import ConnectedPhoneNumberInput from '../../FormElements/ConnectedPhoneNumberInput'
import { PersonalInfoFormValidation } from '../../../formValidation'
import { NewCustomerType } from '../../../types'
import axios from 'axios'
import { getJWT } from '../../../helpers/auth'
import { UserProperty } from '../../../generated/graphql'
import DropDownInput from '../../FormElements/DropDownInput'
import { AGENT_ASSISTING_OPTIONS, TOO_REASON_OPTIONS } from '../../../constants'

type StepTwoProps = {
  stepCount: number
  totalStep: number
  title: string
  description: string
  handleBack: () => void
  handleNext: (e: NewCustomerType) => void
  newCustomer?: NewCustomerType
  activeProperty?: UserProperty
}

export const StepTwo: FC<StepTwoProps> = ({
  stepCount,
  totalStep,
  title,
  description,
  handleBack,
  handleNext,
  newCustomer,
  activeProperty
}: StepTwoProps) => {
  const [isLoadingLocal, setIsLoadingLocal] = useState<boolean>(false)
  const [isIDtoggle, setIDToggle] = useState<boolean>(true)
  const [initialValues, setInitialValues] = useState<NewCustomerType>(() => ({
    firstName: newCustomer?.firstName || '',
    lastName: newCustomer?.lastName || '',
    email: newCustomer?.email || '',
    contactNumber: newCustomer?.contactNumber || '',
    idNumber: newCustomer?.idNumber || '',
    emailAddress: newCustomer?.email || '',
    transferDate: newCustomer?.transferDate || '',
    // idNumber: user?.idPassportNumber || '',
    reason: newCustomer?.reason || '',
    reasonDescription: newCustomer?.reasonDescription || '',
    agent: newCustomer?.agent || '',
    isID: isIDtoggle || false
  }))

  const handleStartTOOProcess = useCallback(
    async (data: NewCustomerType) => {
      console.log(`newCustomer: `, data)

      const bodyData = { ...data }
      setIsLoadingLocal(true)

      const API_BASE_URL = process.env.REACT_APP_API_HOST

      console.log(`bodyData: `, bodyData)

      const resp = await axios.post(
        `${API_BASE_URL}/too-start-process`,
        {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          contactNumber: data.contactNumber,
          passwordHash: null,
          idPassportNumber: data.idNumber,
          expectedMoveDate: data.transferDate,
          transferOfOwnershipStep: 'started',
          transferOfOwnershipCompleted: false,
          recordID: activeProperty?.recordId,
          agentAssisting: data?.agent
        },
        {
          headers: {
            Authorization: getJWT()
          }
        }
      )

      if (resp.status !== 200) {
        throw new Error('too-start-process ---> Error starting the process')
      }

      console.log(`too-start-process ---> resp.data: `, resp.data)

      const { status, message } = resp.data

      setIsLoadingLocal(false)

      handleNext({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        contactNumber: data.contactNumber,
        idNumber: data.idNumber,
        transferDate: data.transferDate,
        reason: data.reason,
        reasonDescription: data.reasonDescription,
        isID: isIDtoggle,
        agent: data.agent
      })

      return { status, message }
    },
    [newCustomer, activeProperty?.recordId, handleNext, isIDtoggle]
  )

  return (
    <div className={`w-full h-auto flex flex-col gap-y-4`}>
      <HeadingView
        stepCount={stepCount}
        totalStep={totalStep}
        title={title}
        description={description}
      />

      <div className="w-full h-auto p-12 pb-6 bg-white rounded-[32px] shadow-sm border-solid border-[1.5px] border-[#e4e4e4]">
        <Formik
          validationSchema={PersonalInfoFormValidation}
          initialValues={initialValues}
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={async (
            {
              firstName,
              lastName,
              email,
              contactNumber,
              idNumber,
              transferDate,
              reason,
              reasonDescription,
              isID,
              agent
            },
            { setSubmitting, setStatus }
          ) => {
            try {
              handleStartTOOProcess({
                firstName,
                lastName,
                email,
                contactNumber,
                idNumber,
                transferDate,
                reason,
                reasonDescription,
                agent
              })

              setStatus(null)
              setSubmitting(true)

              console.log(`handleStartTOOProcess: agent `, agent)

              setSubmitting(false)
            } catch (error: any) {
              setStatus(error.message)
            }
          }}
        >
          {({ isSubmitting, setFieldValue }: FormikProps<NewCustomerType>) => {
            return (
              <Form style={{ width: '100%' }}>
                <Flex w="full" direction={'column'} gap="25px" mb="25px">
                  <Flex
                    direction={['column', 'column', 'row', 'row']}
                    justifyContent={'space-between'}
                    gap={['25px', '25px', '25px', '10%']}
                  >
                    <TextInput name="firstName" placeholder="First name" label="First name" />
                    <TextInput name="lastName" placeholder="Last name" label="Last name" />
                  </Flex>
                  <Flex
                    direction={['column', 'column', 'row', 'row']}
                    justifyContent={'space-between'}
                    gap={['25px', '25px', '25px', '10%']}
                  >
                    <ConnectedPhoneNumberInput
                      name="contactNumber"
                      label="Phone number"
                      placeholder="Enter phone number"
                      bg="transparent"
                      mb={[4, 8]}
                    />
                    <div className="w-full h-auto flex flex-col">
                      <div className="w-[300px] h-fit flex flex-row justify-center bg-[#f0f0f0] border-solid border-[1.25px] border-black rounded-full self-center">
                        <div
                          onClick={() => {
                            setIDToggle(true)
                            setFieldValue('isID', true)
                          }}
                          className={`w-full h-auto text-black text-[12px] tSM:text-[10px] ${
                            isIDtoggle ? 'bg-[#FFD200]' : ''
                          } font-regular rounded-full font-poppins cursor-pointer text-center active:scale-[1.02] hover:bg-[#FFD200] px-4 py-1 tSM:px-3 mMD:px-2`}
                        >{`Use ID`}</div>

                        <div
                          onClick={() => {
                            setIDToggle(false)
                            setFieldValue('isID', false)
                          }}
                          className={`w-full h-auto text-black text-[12px] tSM:text-[10px]  ${
                            !isIDtoggle ? 'bg-[#FFD200]' : ''
                          } font-regular rounded-full font-poppins cursor-pointer text-center active:scale-[1.02] hover:bg-[#FFD200] px-4 py-1 tSM:px-3 mMD:px-2`}
                        >{`Passport`}</div>
                      </div>
                      <TextInput
                        name="idNumber"
                        label={isIDtoggle ? 'ID number' : 'Passport number'}
                        placeholder={
                          isIDtoggle ? 'Enter your ID number' : 'Enter your passport number'
                        }
                      />
                    </div>
                  </Flex>

                  <Flex
                    direction={['column', 'column', 'row', 'row']}
                    justifyContent={'space-between'}
                    gap={['25px', '25px', '25px', '10%']}
                  >
                    <TextInput
                      name="email"
                      label="Email address"
                      placeholder="Enter email address"
                    />
                  </Flex>

                  <Flex
                    direction={['column', 'column', 'row', 'row']}
                    justifyContent={'space-between'}
                    gap={['25px', '25px', '25px', '10%']}
                  >
                    <TextInput
                      type={'date'}
                      name="transferDate"
                      placeholder="Expected move / transfer date"
                      label="Expected move / transfer date"
                    />
                  </Flex>

                  <Flex
                    direction={['column', 'column', 'row', 'row']}
                    justifyContent={'space-between'}
                    gap={['25px', '25px', '25px', '10%']}
                  >
                    <DropDownInput
                      name="reason"
                      label="Reason for home transfer"
                      options={TOO_REASON_OPTIONS}
                      placeholder="Enter reason here"
                    />
                    <TextInput
                      name="reasonDescription"
                      label="Descrption for home transfer"
                      placeholder="Enter descrption reason here"
                      type="text"
                    />
                  </Flex>

                  <Flex
                    direction={['column', 'column', 'row', 'row']}
                    justifyContent={'space-between'}
                    gap={['25px', '25px', '25px', '10%']}
                  >
                    <DropDownInput
                      name="agent"
                      label="Agent assisting"
                      options={AGENT_ASSISTING_OPTIONS}
                      placeholder="Which agent is assisting you?"
                    />
                    <div className="w-full h-auto"></div>
                  </Flex>

                  <Flex
                    direction={['column', 'column', 'row', 'row']}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    gap={['25px', '25px', '25px', '10%']}
                  >
                    <div className="w-fit h-fit flex flex-row justify-between">
                      <BackButton label="Back" handleClick={handleBack} isBack={false}>
                        <IoMdArrowBack className="w-[20px] h-[20px]" color="#000000" />
                      </BackButton>
                    </div>

                    <Flex w="100%" h="100%" alignItems={'center'} justifyContent="flex-end">
                      <Button variant={'dark'} isLoading={isLoadingLocal} type="submit" mt="15px">
                        Save changes
                      </Button>
                    </Flex>
                  </Flex>
                </Flex>
              </Form>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}

export default StepTwo
